import { TpSlOrderFormState } from 'src/components/TradeForm/TpSlOrderForm/hooks/useTpSlOrderFormState';
import { Dispatch, SetStateAction, useCallback } from 'react';
import {
	BigNum,
	ONE,
	PRICE_PRECISION_EXP,
	PositionDirection,
} from '@drift-labs/sdk';
import { MarketId, UIOrderType } from '@drift/common';
import { SaveTpSlOrdersCallback } from 'src/components/TradeForm/TpSlOrderForm/types';
import { validateTpSlOrder } from 'src/utils/tpSlValidation';
import useGetTickSizeForMarket, {
	getTickSizeDecimalPlaces,
} from 'src/hooks/useGetTickSizeForMarket';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';

interface Params {
	calcPercent: (price: BigNum) => number;
	calcPrice: (percentMove: number, isTakeProfit: boolean) => BigNum;
	clearTakeProfitState: () => void;
	clearStopLossState: () => void;
	setTakeProfitState: Dispatch<SetStateAction<TpSlOrderFormState>>;
	setStopLossState: Dispatch<SetStateAction<TpSlOrderFormState>>;
	positionIsLong: boolean;
	entryPrice: BigNum;
	markPrice: BigNum;
	takeProfitPriceStringValue: string;
	takeProfitInvalid: boolean;
	stopLossPriceStringValue: string;
	stopLossInvalid: boolean;
	takeProfitPrice: BigNum | null;
	takeProfitPercent: number | null;
	takeProfitLimitPrice?: BigNum | null;
	stopLossPrice: BigNum | null;
	stopLossPercent: number | null;
	stopLossLimitPrice?: BigNum | null;
	savedTakeProfitPrice: BigNum | null | undefined;
	savedStopLossPrice: BigNum | null | undefined;
	triggerOrderDirection: PositionDirection;
	saveOrdersCallback: SaveTpSlOrdersCallback;
	allowTrailingStopLoss?: boolean;
	marketIndex?: number;
}

export default function useTpSlOrderFormHandlers({
	calcPercent,
	calcPrice,
	clearTakeProfitState,
	clearStopLossState,
	setTakeProfitState,
	setStopLossState,
	positionIsLong,
	entryPrice,
	markPrice,
	takeProfitPriceStringValue,
	takeProfitInvalid,
	stopLossPriceStringValue,
	stopLossInvalid,
	takeProfitPrice,
	takeProfitPercent,
	takeProfitLimitPrice,
	stopLossPrice,
	stopLossPercent,
	stopLossLimitPrice,
	savedTakeProfitPrice,
	savedStopLossPrice,
	triggerOrderDirection,
	saveOrdersCallback,
	allowTrailingStopLoss = false,
	marketIndex,
}: Params) {
	const selectedMarketId = useDriftStore(
		(s) => s.selectedMarket.current?.marketId
	);
	const getTickSizeForMarket = useGetTickSizeForMarket();
	// Use marketIndex if provided, otherwise fall back to selectedMarketId from the store
	const effectiveMarketId =
		marketIndex !== undefined
			? MarketId.createPerpMarket(marketIndex)
			: selectedMarketId;

	const tickSize = effectiveMarketId
		? getTickSizeForMarket(effectiveMarketId)
		: ONE;
	const tickSizeDecimalPlaces = getTickSizeDecimalPlaces(tickSize);

	const updateOrders = useCallback(() => {
		const takeProfit = (() => {
			if (
				!takeProfitPriceStringValue ||
				!takeProfitPrice ||
				takeProfitInvalid
			) {
				return undefined;
			}

			// Determine if this is a limit order
			const isLimitOrder = takeProfitLimitPrice ? true : false;
			// For limit orders, use the current trigger price if available, otherwise fall back to saved
			// For market orders, use the current price as trigger price
			const triggerPrice = isLimitOrder
				? takeProfitPrice || savedTakeProfitPrice
				: takeProfitPrice;

			if (triggerPrice?.gtZero() || takeProfitPercent) {
				return {
					direction: triggerOrderDirection,
					price: triggerPrice || null,
					percent: parseFloat((takeProfitPercent ?? 0).toFixed(2)),
					orderType: isLimitOrder ? ('limit' as const) : ('market' as const),
					limitPrice: takeProfitLimitPrice || null,
				};
			}
			if (savedTakeProfitPrice) {
				return {
					direction: triggerOrderDirection,
					price: savedTakeProfitPrice,
					orderType: isLimitOrder ? ('limit' as const) : ('market' as const),
					limitPrice: takeProfitLimitPrice || null,
				};
			}
		})();

		const stopLoss = (() => {
			if (!stopLossPriceStringValue || stopLossInvalid || !stopLossPrice) {
				return undefined;
			}

			// Determine if this is a limit order
			const isLimitOrder = stopLossLimitPrice ? true : false;

			// For limit orders, use the current trigger price if available, otherwise fall back to saved
			// For market orders, use the current price as trigger price
			const triggerPrice = isLimitOrder
				? stopLossPrice || savedStopLossPrice
				: stopLossPrice;

			if (triggerPrice?.gtZero() || stopLossPercent) {
				return {
					direction: triggerOrderDirection,
					price: triggerPrice || null,
					percent: parseFloat((stopLossPercent ?? 0).toFixed(2)),
					orderType: isLimitOrder ? ('limit' as const) : ('market' as const),
					limitPrice: stopLossLimitPrice || null,
				};
			}

			if (savedStopLossPrice) {
				return {
					direction: triggerOrderDirection,
					price: savedStopLossPrice,
					orderType: isLimitOrder ? ('limit' as const) : ('market' as const),
					limitPrice: stopLossLimitPrice || null,
				};
			}
		})();

		const orders = {
			takeProfit,
			stopLoss,
			anyInvalid: takeProfitInvalid || stopLossInvalid,
		};

		saveOrdersCallback(orders);
	}, [
		takeProfitPriceStringValue,
		takeProfitInvalid,
		stopLossPriceStringValue,
		stopLossInvalid,
		takeProfitPrice,
		takeProfitPercent,
		takeProfitLimitPrice,
		stopLossPercent,
		stopLossPrice,
		stopLossLimitPrice,
		savedTakeProfitPrice,
		savedStopLossPrice,
		triggerOrderDirection,
		saveOrdersCallback,
	]);

	const validateTpSl = useCallback(
		(
			newPriceBigNum: BigNum,
			isTakeProfit: boolean
		): {
			invalid: boolean;
			reason: 'entry' | 'immediate' | 'reference' | null;
		} => {
			// Use the shared utility function
			const orderType = isTakeProfit ? 'takeProfit' : 'stop';
			const positionDirection = positionIsLong ? 'long' : 'short';

			// Note: markPriceRef.current should match the "Reference Price" shown in the modal
			// The modal calculates this using getTriggerPrice() from the SDK, but this hook
			// receives it as markPrice parameter from the parent useTpSlOrderForm hook
			const validation = validateTpSlOrder(
				newPriceBigNum,
				orderType,
				positionDirection,
				entryPrice,
				markPrice, // This should be the same as Reference Price in modal
				allowTrailingStopLoss
			);

			return {
				invalid: validation.invalid,
				reason: validation.reason,
			};
		},
		[positionIsLong, markPrice, entryPrice, allowTrailingStopLoss]
	);

	const updatePrice = useCallback(
		(newValue: string, isTakeProfit: boolean, orderType?: UIOrderType) => {
			const isLimitOrder = orderType === 'limit';

			if (!newValue) {
				if (isLimitOrder) {
					// For limit orders, only clear trigger price, preserve % and limit price
					const setState = isTakeProfit ? setTakeProfitState : setStopLossState;
					setState((prevState) => ({
						...prevState,
						priceStringValue: '',
						price: null,
					}));
				} else {
					// For market orders, clear everything
					isTakeProfit ? clearTakeProfitState() : clearStopLossState();
				}
				updateOrders();
				return;
			}
			const newPriceBigNum = BigNum.fromPrint(
				parseFloat(newValue).toFixed(tickSizeDecimalPlaces),
				PRICE_PRECISION_EXP
			);

			const newPercent = calcPercent(newPriceBigNum);

			const { invalid: isNewValueInvalid, reason: invalidReason } =
				validateTpSl(newPriceBigNum, isTakeProfit);

			const setState = isTakeProfit ? setTakeProfitState : setStopLossState;

			if (isNewValueInvalid) {
				setState(
					(prevState) =>
						({
							...prevState,
							percentStringValue: isLimitOrder
								? prevState.percentStringValue
								: '',
							percent: isLimitOrder ? prevState.percent : 0,
							price: null,
							priceInvalid: true,
							priceInvalidReason: invalidReason,
							priceStringValue: newValue,
						}) satisfies TpSlOrderFormState
				);
			} else {
				setState(
					(prevState) =>
						({
							...prevState,
							percentStringValue: isLimitOrder
								? prevState.percentStringValue
								: newPercent.toFixed(2),
							percent: isLimitOrder ? prevState.percent : newPercent,
							price: newPriceBigNum,
							priceInvalid: false,
							priceInvalidReason: null,
							priceStringValue: newValue,
							inputSource: 'price',
						}) satisfies TpSlOrderFormState
				);
			}
			updateOrders();
		},
		[
			tickSizeDecimalPlaces,
			calcPercent,
			setTakeProfitState,
			setStopLossState,
			clearTakeProfitState,
			clearStopLossState,
			positionIsLong,
			entryPrice,
			validateTpSl,
			updateOrders,
		]
	);

	const updatePercent = useCallback(
		(newValue: string, isTakeProfit: boolean, orderType?: UIOrderType) => {
			if (!newValue) {
				isTakeProfit ? clearTakeProfitState() : clearStopLossState();
				updateOrders();
				return;
			}

			const newPercent = parseFloat(newValue);

			// Check if the parsed value is a valid number
			if (isNaN(newPercent)) {
				return;
			}

			let newPrice = calcPrice(newPercent, isTakeProfit);

			if (newPrice.ltZero()) {
				newPrice = BigNum.zero();
			}

			const setState = isTakeProfit ? setTakeProfitState : setStopLossState;

			const isLimitOrder = orderType === 'limit';

			const { invalid, reason } = validateTpSl(newPrice, isTakeProfit);

			if (invalid) {
				setState(
					(prevState) =>
						({
							...prevState,
							percentStringValue: newValue,
							priceStringValue: newPrice.toNum().toFixed(tickSizeDecimalPlaces),
							limitPriceStringValue: isLimitOrder
								? newPrice.toNum().toFixed(tickSizeDecimalPlaces)
								: prevState.limitPriceStringValue,
							percent: newPercent,
							price: null,
							limitPrice: isLimitOrder ? null : prevState.limitPrice,
							limitPriceInvalid: isLimitOrder
								? true
								: prevState.limitPriceInvalid,
							limitPriceInvalidReason: isLimitOrder
								? reason
								: prevState.limitPriceInvalidReason,
							priceInvalid: isLimitOrder ? prevState.priceInvalid : true,
							priceInvalidReason: isLimitOrder
								? prevState.priceInvalidReason
								: reason,
						}) satisfies TpSlOrderFormState
				);
				updateOrders();
				return;
			}

			setState(
				(prevState) =>
					({
						...prevState,
						percentStringValue: newValue,
						priceStringValue: newPrice.toNum().toFixed(tickSizeDecimalPlaces),
						percent: newPercent,
						price: newPrice,
						limitPrice: isLimitOrder ? newPrice : prevState.limitPrice,
						limitPriceStringValue: isLimitOrder
							? newPrice.toNum().toFixed(tickSizeDecimalPlaces)
							: prevState.limitPriceStringValue,
						limitPriceInvalid: isLimitOrder
							? false
							: prevState.limitPriceInvalid,
						limitPriceInvalidReason: isLimitOrder
							? null
							: prevState.limitPriceInvalidReason,
						priceInvalid: isLimitOrder ? prevState.priceInvalid : false,
						priceInvalidReason: isLimitOrder
							? prevState.priceInvalidReason
							: null,
						inputSource: 'percent',
					}) satisfies TpSlOrderFormState
			);
			// Call updateOrders asynchronously to avoid circular dependency
			updateOrders();
		},
		[
			clearTakeProfitState,
			clearStopLossState,
			setTakeProfitState,
			setStopLossState,
			calcPrice,
			tickSizeDecimalPlaces,
			positionIsLong,
			entryPrice,
			validateTpSl,
			allowTrailingStopLoss,
			updateOrders,
		]
	);

	const updateLimitPrice = useCallback(
		(newValue: string, isTakeProfit: boolean) => {
			const setState = isTakeProfit ? setTakeProfitState : setStopLossState;

			if (!newValue) {
				// When limit price is cleared, also clear trigger price and %
				setState((prevState) => ({
					...prevState,
					limitPriceStringValue: '',
					limitPrice: null,
					priceStringValue: '',
					price: null,
					percentStringValue: '',
					percent: 0,
				}));
				updateOrders();
				return;
			}

			const newLimitPriceBigNum = BigNum.fromPrint(
				parseFloat(newValue).toFixed(tickSizeDecimalPlaces),
				PRICE_PRECISION_EXP
			);

			// Validate the limit price
			const { invalid, reason } = validateTpSl(
				newLimitPriceBigNum,
				isTakeProfit
			);

			if (invalid) {
				setState(
					(prevState) =>
						({
							...prevState,
							limitPriceStringValue: newValue,
							limitPrice: null,
							percentStringValue: '',
							percent: 0,
							limitPriceInvalid: true,
							limitPriceInvalidReason: reason,
						}) satisfies TpSlOrderFormState
				);
				updateOrders();
				return;
			}

			const { newPercent, newPercentStringValue } = (() => {
				if (!entryPrice || entryPrice.eqZero()) {
					return { newPercent: 0, newPercentStringValue: '' };
				}

				const percent = calcPercent(newLimitPriceBigNum);

				return {
					newPercent: percent,
					newPercentStringValue: percent.toFixed(2),
				};
			})();

			setState(
				(prevState) =>
					({
						...prevState,
						limitPriceStringValue: newValue,
						limitPrice: newLimitPriceBigNum,
						percentStringValue: newPercentStringValue,
						percent: newPercent,
						limitPriceInvalid: false,
						limitPriceInvalidReason: null,
					}) satisfies TpSlOrderFormState
			);
			updateOrders();
		},
		[
			setTakeProfitState,
			setStopLossState,
			tickSizeDecimalPlaces,
			entryPrice,
			validateTpSl,
			updateOrders,
			calcPercent,
		]
	);

	return {
		updatePrice,
		updatePercent,
		updateLimitPrice,
		updateOrders,
	};
}
